import axios from "axios";
import { COACH_REQUEST_TIMEOUT_MS } from "./coachWait";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// --- Auth: attach the Bearer token (localStorage) to every request. ---
export const AUTH_TOKEN_KEY = "compass_auth_token";
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
  // WPI LIVE (developer-gated): when the dev flag is on, mark ordinary draft submits so the server
  // runs off-path detection + enforces the re-entry gate. Never sent for normal students.
  try {
    if (localStorage.getItem("wpi_dev") === "1" && typeof config.url === "string"
        && /\/sessions\/[^/]+\/interact$/.test(config.url) && config.data && typeof config.data === "object") {
      config.data = { ...config.data, wpi_dev: true };
    }
  } catch (e) { /* ignore */ }
  return config;
});

// WPI LIVE: surface the server-side gate immediately. When an ordinary submit is refused because a
// Learning Re-entry is required, broadcast it so the overlay opens without waiting for the next poll.
axios.interceptors.response.use((r) => r, (error) => {
  try {
    const d = error?.response?.data?.detail;
    if (d && d.reentry_required) {
      window.dispatchEvent(new CustomEvent("wpi-reentry-required", { detail: d }));
    }
    if (d && d.aes_required) {
      window.dispatchEvent(new CustomEvent("aes-required", { detail: d }));
    }
    // Daily AI limit (server-enforced): one plain-language notice, not a technical error.
    if (error?.response?.status === 429 && error.response.data?.limit_scope && typeof d === "string") {
      import("sonner").then(({ toast }) => toast.warning(d, { id: "ai-daily-limit", duration: 9000 })).catch(() => {});
    }
  } catch (e) { /* ignore */ }
  return Promise.reject(error);
});

export const registerAccount = async (payload) => {
  const { data } = await axios.post(`${API}/auth/register`, payload);
  return data; // { token, user }
};
export const loginAccount = async (payload) => {
  const { data } = await axios.post(`${API}/auth/login`, payload);
  return data; // { token, user }
};
export const fetchMe = async () => {
  const { data } = await axios.get(`${API}/auth/me`);
  return data;
};
export const fetchMySessions = async () => {
  const { data } = await axios.get(`${API}/auth/my-sessions`);
  return data.sessions || [];
};
export const claimSession = async (id) => {
  const { data } = await axios.post(`${API}/sessions/${id}/claim`);
  return data;
};
export const renameSession = async (id, title) => {
  const { data } = await axios.patch(`${API}/sessions/${id}/title`, { title });
  return data;
};
export const forgotPassword = async (email) => {  const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
  return data;
};
export const resetPassword = async (token, new_password) => {
  const { data } = await axios.post(`${API}/auth/reset-password`, { token, new_password });
  return data;
};
// --- Beta access: invitations (public accept by token; admin-only management) ---
export const previewInvitation = async (token) => (await axios.get(`${API}/invitations/${encodeURIComponent(token)}`)).data;
export const acceptInvitation = async (token, p) => (await axios.post(`${API}/invitations/${encodeURIComponent(token)}/accept`, p)).data;
export const adminApplyInvitation = async (id) => (await axios.post(`${API}/admin/invitations/${id}/apply`)).data;         // pending invite → the existing account with that email
export const adminUsersBulk = async (ids, action) => (await axios.post(`${API}/admin/users/bulk`, { ids, action })).data;   // { done, failed }
export const adminInvitationsBulk = async (ids, action) => (await axios.post(`${API}/admin/invitations/bulk`, { ids, action })).data;
export const cRemoveStudents = async (courseId, student_ids) => (await axios.post(`${API}/classroom/courses/${courseId}/students/remove`, { student_ids })).data;
export const adminListInvitations = async (status = "") =>
  (await axios.get(`${API}/admin/invitations`, { params: status ? { status } : {} })).data.invitations || [];
export const adminCreateInvitations = async (p) => (await axios.post(`${API}/admin/invitations`, p)).data.invitations || [];
export const adminResendInvitation = async (id) => (await axios.post(`${API}/admin/invitations/${id}/resend`)).data;
export const adminRevokeInvitation = async (id) => (await axios.post(`${API}/admin/invitations/${id}/revoke`)).data;
// --- Admin oversight (administrators only): people, their courses / work, account actions ---
export const adminOverview = async () => (await axios.get(`${API}/admin/overview`)).data;
export const adminAiLimits = async () => (await axios.get(`${API}/admin/ai-limits`)).data;
export const adminSelectorPrototype = async (days = 14) => (await axios.get(`${API}/admin/selector-prototype`, { params: { days } })).data;
export const adminUsers = async (params = {}) => (await axios.get(`${API}/admin/users`, { params })).data.users || [];
export const adminUserCourses = async (uid) => (await axios.get(`${API}/admin/users/${uid}/courses`)).data;
export const adminUserWork = async (uid) => (await axios.get(`${API}/admin/users/${uid}/work`)).data;
export const adminApproveUser = async (uid) => (await axios.post(`${API}/admin/users/${uid}/approve`)).data;
export const adminDeactivateUser = async (uid) => (await axios.post(`${API}/admin/users/${uid}/deactivate`)).data;
export const adminReactivateUser = async (uid) => (await axios.post(`${API}/admin/users/${uid}/reactivate`)).data;
export const adminSetRole = async (uid, role) => (await axios.post(`${API}/admin/users/${uid}/role`, { role })).data;
export const adminArchiveUser = async (uid) => (await axios.post(`${API}/admin/users/${uid}/archive`)).data;
export const adminRestoreUser = async (uid) => (await axios.post(`${API}/admin/users/${uid}/restore`)).data;
export const adminDeletedAssignments = async () => (await axios.get(`${API}/admin/deleted-assignments`)).data;
export const adminRestoreAssignment = async (aid) => (await axios.post(`${API}/admin/deleted-assignments/${aid}/restore`)).data;
export const adminResetLink = async (uid) => (await axios.post(`${API}/admin/users/${uid}/reset-link`)).data;


export const createSession = async (payload) => {
  const { data } = await axios.post(`${API}/sessions`, payload);
  return data;
};

// Download the paragraph as a real Word (.docx) generated server-side (preserves paragraph structure).
export const downloadParagraphDocx = async (content, title = "", filename = "paragraph.docx") => {
  const res = await axios.post(
    `${API}/export/paragraph.docx`,
    { content, title },
    { responseType: "blob" }
  );
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const startPreview = async (opts) => {
  const { data } = await axios.post(`${API}/sessions/preview`, opts || {});
  return data;
};

// DEV-ONLY: direct Sentence Craft test entry (bypasses composition scaffolding).
export const previewContinue = async (id) => {
  const { data } = await axios.post(`${API}/sessions/${id}/preview-continue`);
  return data;
};

// TWO-ROUND bounded experience — round choice + cumulative review + dev diagnostics.
export const roundChoice = async (id, choice, content) => {
  const body = content !== undefined && content !== null ? { choice, content } : { choice };
  const { data } = await axios.post(`${API}/sessions/${id}/round`, body);
  return data;
};
// TESTING BRIDGE — OT/FO → old-composition functional paragraph teaching (see recovery audit).
export const beginCompositionBridge = async (id, draft, inputTelemetry) => {
  const body = draft ? { draft, input_telemetry: inputTelemetry || null } : {};
  const { data } = await axios.post(`${API}/sessions/${id}/composition-bridge/begin`, body);
  return data;
};
export const advanceCompositionBridge = async (id) => {
  const { data } = await axios.post(`${API}/sessions/${id}/composition-bridge/advance`);
  return data;
};
export const getCompositionBridge = async (id) => {
  const { data } = await axios.get(`${API}/sessions/${id}/composition-bridge`);
  return data;
};

// FUNCTIONAL ORGANIZATION (Phase 2, Checkpoint 1) — multi-turn FO teaching stage (OT -> FO -> Composition).
export const startFO = async (id) => {
  const { data } = await axios.post(`${API}/fo/${id}/start`);
  return data;
};
export const getFO = async (id) => {
  const { data } = await axios.get(`${API}/fo/${id}/state`);
  return data;
};
export const foCoach = async (id, message = "", event = "") => {
  const { data } = await axios.post(`${API}/fo/${id}/coach`, { message, event });
  return data;
};
export const completeFO = async (id) => {
  const { data } = await axios.post(`${API}/fo/${id}/complete`);
  return data;
};

// CASCADE RETURN-AND-RESUME (Phase 2, Checkpoint 2) — step back to an earlier process and resume in place.
export const cascadeReturn = async (id, payload) => {
  const { data } = await axios.post(`${API}/cascade/${id}/return`, payload);
  return data;
};
export const getResumeStatus = async (id) => {
  const { data } = await axios.get(`${API}/cascade/${id}/resume-status`);
  return data;
};
export const cascadeResume = async (id) => {
  const { data } = await axios.post(`${API}/cascade/${id}/resume`);
  return data;
};

// CASCADE COORDINATOR (Phase 2) — read-only developmental-process orientation snapshot.
export const getCascade = async (id, view = "") =>
  (await axios.get(`${API}/sessions/${id}/cascade${view ? `?view=${encodeURIComponent(view)}` : ""}`)).data;
export const getEpisodeReview = async (id) => {
  const { data } = await axios.get(`${API}/sessions/${id}/episode-review`);
  return data;
};
// PUBLIC marketing Teacher Review — safe seeded demo payload (no session id, read-only).
export const getTeacherReviewDemo = async () => {
  const { data } = await axios.get(`${API}/public/teacher-review-demo`);
  return data;
};
export const getConstitution = async () => {
  const { data } = await axios.get(`${API}/compass/constitution`);
  return data;
};

export const getSession = async (id) => {
  const { data } = await axios.get(`${API}/sessions/${id}`);
  return data;
};

export const getTeacherReflection = async (id) => {
  const { data } = await axios.get(`${API}/sessions/${id}/teacher-reflection`);
  return data;
};

// Z6 — teacher-visible Level-5 scaffolding record (shadow-derived instructional signal; not a grade).
export const getDevelopmentalRange = async (id) => {
  const { data } = await axios.get(`${API}/sessions/${id}/developmental-range`);
  return data;
};

export const getScaffoldingFlags = async (id) => {
  const { data } = await axios.get(`${API}/sessions/${id}/scaffolding-flags`);
  return data;
};

// DEV-ONLY (Sprint 4.0-1 calibration): the functional_v3 reasoning trace, including the hidden
// Developmental Cognition Object per turn. Not linked from normal UI.
export const getNoticing = async (id) => {
  const { data } = await axios.post(`${API}/sessions/${id}/noticing`);
  return data;
};

// --- Organizing Thought (OT) — five persistent objects before Writing ---
// CUTOVER: the consolidated teaching loop (appraiser -> controller -> ACT) is the Full Studio's pathway. `?loop=old` opts a
// browser back onto the old pathway (kept intact, not purged); `?loop=new` returns to the default.
export const wantsNewLoop = () => {
  try {
    const q = new URLSearchParams(window.location.search).get("loop");
    if (q === "old") return false;
    if (q === "new") return true;
    return localStorage.getItem("tws_new_loop") !== "0";
  } catch (e) { return true; }
};

export const otStart = async (id) => {
  // CUTOVER SWITCH: the consolidated loop is stamped on the session at the start (sticky), before any turn
  const { data } = await axios.post(`${API}/ot/${id}/start${wantsNewLoop() ? "?loop=new" : ""}`);
  return data.ot;
};

export const otSaveObject = async (id, stage, content) => {
  const { data } = await axios.post(`${API}/ot/${id}/object`, { stage, content });
  return data.ot;
};

export const otInteract = async (id, stage, content) => {
  const { data } = await axios.post(`${API}/ot/${id}/interact`, { stage, content });
  return data; // { ot, decision, message, sufficiency }
};

export const otAdvance = async (id, toStage) => {
  const { data } = await axios.post(`${API}/ot/${id}/advance`, { to_stage: toStage });
  return data.ot;
};

export const otHandoff = async (id) => {
  const { data } = await axios.post(`${API}/ot/${id}/handoff`);
  return data.ot;
};

// --- OT Thinking Workspace (redesign v1.0) — one persistent, student-owned representation ---
export const wsGet = async (id) => (await axios.get(`${API}/ot/${id}/ws`)).data; // { ot, sufficiency }
// DEV/DEBUG (Phase 2B shadow observability) — read-only; never exposed in the student UI.
export const wsAddItem = async (id, body) => (await axios.post(`${API}/ot/${id}/ws/item`, body)).data; // { item, ot }
export const wsPatchItem = async (id, itemId, patch) => (await axios.patch(`${API}/ot/${id}/ws/item/${itemId}`, patch)).data; // { item, ot }
export const wsSeparateItem = async (id, itemId, parts) => (await axios.post(`${API}/ot/${id}/ws/item/${itemId}/separate`, { parts })).data; // { items, ot }
export const wsJoinItems = async (id, itemIds) => (await axios.post(`${API}/ot/${id}/ws/join`, { item_ids: itemIds })).data; // { item, ot }
export const wsSetSection = async (id, itemId, node_kind, composition_function = "") => (await axios.post(`${API}/ot/${id}/ws/item/${itemId}/section`, { node_kind, composition_function })).data; // { item, ot } — living-workspace section/subsection/function
export const wsDeleteItem = async (id, itemId) => (await axios.delete(`${API}/ot/${id}/ws/item/${itemId}`)).data; // { ot }
export const wsReorder = async (id, body) => (await axios.post(`${API}/ot/${id}/ws/reorder`, body)).data; // { ot } — body: { array, id, before_id }
export const wsRegionStart = async (id, itemId, region) => (await axios.post(`${API}/ot/${id}/ws/regions/start`, { item_id: itemId, region })).data; // { ot, prev, changed } — ONE boundary assertion
export const wsRegionsRestore = async (id, items) => (await axios.post(`${API}/ot/${id}/ws/regions/restore`, { items })).data; // { ot } — one-step undo of a region operation
export const wsCoverageGet = async (id) => (await axios.get(`${API}/ot/${id}/ws/coverage`)).data; // { due, status, question, … }
export const wsCoverageConfirm = async (id, choice, note = "") => (await axios.post(`${API}/ot/${id}/ws/coverage`, { choice, note })).data;
export const wsReorderSiblings = async (id, order) => (await axios.post(`${API}/ot/${id}/ws/reorder-siblings`, { order })).data; // { ot } — order: [{type,id},…] unified sibling sequence
export const wsAddRel = async (id, body) => (await axios.post(`${API}/ot/${id}/ws/relationship`, body)).data; // { relationship, ot }
export const wsPatchRel = async (id, relId, body) => (await axios.patch(`${API}/ot/${id}/ws/relationship/${relId}`, body)).data; // { relationship, ot }
export const wsDeleteRel = async (id, relId) => (await axios.delete(`${API}/ot/${id}/ws/relationship/${relId}`)).data; // { ot }
export const wsAnswer = async (id, text) => (await axios.post(`${API}/ot/${id}/ws/answer`, { text })).data; // { ot }
export const wsCoach = async (id, message = "", event = "", phase = "", extra = {}) => {
  let phase2c = false;
  const new_loop = wantsNewLoop();
  try {
    phase2c = (localStorage.getItem("tws_phase2c") === "1")
      || new URLSearchParams(window.location.search).get("tws2c") === "1";
  } catch (e) { /* ignore */ }
  // a coach turn may chain several model calls; a client-side ceiling lets a hung request surface as a TIMEOUT
  // (slow processing) rather than hanging forever — the student's typed text is preserved by the caller
  return (await axios.post(`${API}/ot/${id}/ws/coach`, { message, event, phase, phase2c, new_loop, ...extra }, { timeout: COACH_REQUEST_TIMEOUT_MS })).data;
}; // { message, highlight, phase_advance, focus_item_ids, focus_question_ids, reflected, sufficiency, structure, phase2c, timing }
// AUTHENTIC REPAIR 1 — read-only recovery of a coach turn whose response was lost to a timeout:
// { status: "done", …coach result } | { status: "processing" } | { status: "not_run" }. Never runs anything.
export const wsTurnStatus = async (id, turnId) => (await axios.get(`${API}/ot/${id}/ws/turn/${encodeURIComponent(turnId)}`)).data;
export const wsRepStatus = async (id, status) => (await axios.post(`${API}/ot/${id}/ws/rep-status`, { status })).data; // { ot, rep_status } — "tws_sufficient" | "continued_unresolved"
export const otTransitionCheck = async (id) => (await axios.post(`${API}/ot/${id}/transition-check`)).data; // { ready, operation, target, target_label }

// Functionally Scaffolded Composition (FSC) — Prototype 1
export const fscSeed = async () => (await axios.post(`${API}/fsc/seed`)).data; // { session_id } (Frankl fixture)
export const fscState = async (id) => (await axios.get(`${API}/fsc/${id}/state`)).data; // { assignment, thinking, fsc }
export const fscWriting = async (id, w) => (await axios.put(`${API}/fsc/${id}/writing`, w)).data; // { writing_state }
export const fscRecruit = async (id, resource_ids, kind = "idea") => (await axios.post(`${API}/fsc/${id}/recruit`, { resource_ids, kind })).data;
export const fscTurn = async (id, body = {}) => (await axios.post(`${API}/fsc/${id}/turn`, body)).data; // { message, support_level, routing, diagnostic, active_target, communicative_state }

// Essay-Level Composition Controller — whole-text organization above the local Composition engine
export const essayState = async (id) => (await axios.get(`${API}/essay/${id}/state`)).data; // { assignment, pathway, thinking, draft, essay }
export const essayTurn = async (id, body = {}) => (await axios.post(`${API}/essay/${id}/turn`, body)).data; // { message, support_level, routing, active_problem, confidence, sufficiency }
export const essaySaveThinking = async (id, text, relates_to = "") => (await axios.post(`${API}/essay/${id}/save-thinking`, { text, relates_to })).data;
export const essayReaderOrder = async (id, part_id, reasoning = "", func = "") => (await axios.post(`${API}/essay/${id}/go/reader-order`, { part_id, reasoning, function: func })).data; // PHASE C2 { message, thought_parts, active_resource_part }
export const essayReturnToThinking = async (id, body = {}) => (await axios.post(`${API}/essay/${id}/return-to-thinking`, body)).data; // PHASE C3 { ok, return_purpose, mode }
export const essayResumeComposition = async (id) => (await axios.post(`${API}/essay/${id}/resume-composition`, {})).data; // PHASE C3 { ok, mode, continuation_context }

// v1.1 problem structure (questions / FEI / relations among questions) + epistemic thesis
export const wsAddQuestion = async (id, body) => (await axios.post(`${API}/ot/${id}/ws/question`, body)).data; // { question, ot }
export const wsPatchQuestion = async (id, qid, patch) => (await axios.patch(`${API}/ot/${id}/ws/question/${qid}`, patch)).data; // { question, ot }
export const wsDeleteQuestion = async (id, qid) => (await axios.delete(`${API}/ot/${id}/ws/question/${qid}`)).data; // { ot }
export const wsAddQuestionRel = async (id, body) => (await axios.post(`${API}/ot/${id}/ws/question-relationship`, body)).data; // { relationship, ot }
export const wsDeleteQuestionRel = async (id, rid) => (await axios.delete(`${API}/ot/${id}/ws/question-relationship/${rid}`)).data; // { ot }
export const wsThesis = async (id, text) => (await axios.post(`${API}/ot/${id}/ws/thesis`, { text })).data; // { ot, structure }
// FO-unification — functional essay regions in the SAME living map (introduction/body/conclusion)
export const wsActivateRegions = async (id) => (await axios.post(`${API}/ot/${id}/ws/regions/activate`)).data; // { ot, structure }
// UDWW Pass 3B — LOCAL COMPOSITION on one unit / branch. The unit stays the authoritative text; coaching is async
// (a pending dialogue entry is filled in the background — poll wsComposeGet while `pending`).
export const wsComposeOpen = async (id, unitId) => (await axios.post(`${API}/ot/${id}/ws/unit/${unitId}/compose/open`)).data; // { ot, compose, target, readiness }
export const wsComposeReturn = async (id, unitId) => (await axios.post(`${API}/ot/${id}/ws/unit/${unitId}/compose/return`)).data; // { ot, compose }
export const wsEmergingWhole = async (id) => (await axios.get(`${API}/ot/${id}/ws/emerging-whole`)).data;   // THE EMERGING WHOLE (read-only derived projection): { main_idea, requirements, realization, thinking, next, early, stale, signature }
export const scratchGet = async (id) => (await axios.get(`${API}/ot/${id}/scratch`)).data;                      // { text, updated_at } — the student's scratch pad (its own collection; never read by the coach or any measurement)
export const scratchPut = async (id, text) => (await axios.put(`${API}/ot/${id}/scratch`, { text })).data;
export const wsIncorporate = async (id, turn_id = "", text = "") => (await axios.post(`${API}/ot/${id}/ws/incorporate`, { turn_id, text })).data; // { item, created, ot, document } — the student's OWN dialogue words → essay, exact, student-authored
export const wsBigPictureClose = async (id, key) => (await axios.post(`${API}/ot/${id}/ws/big-picture/close`, { key })).data;   // WHOLE ORIENTATION: the student closed a TWS-opened Big Picture — that key is dismissed (presentation memory only)
export const wsComposeGet = async (id, unitId) => (await axios.get(`${API}/ot/${id}/ws/unit/${unitId}/compose`)).data; // { unit, compose, target, readiness, children, episode, dialogue, pending, ot }
export const wsComposeTurn = async (id, unitId, body) => (await axios.post(`${API}/ot/${id}/ws/unit/${unitId}/compose`, body)).data; // { item, ot, unit_text, pending, turn_id } — body: { text?, formatting?, kind, message? }
export const wsDeclineRegions = async (id) => (await axios.post(`${API}/ot/${id}/ws/regions/decline`)).data; // { ot }
export const wsThesisRegion = async (id, region) => (await axios.post(`${API}/ot/${id}/ws/thesis/region`, { region })).data; // { ot }
export const wsClearLatent = async (id) => (await axios.post(`${API}/ot/${id}/ws/latent-structure/clear`)).data; // { ot } — clear TWS's externalized latent structure (student's ideas return to loose)
export const wsMakePart = async (id, label, member_ids) => (await axios.post(`${API}/ot/${id}/ws/part`, { label, member_ids })).data; // { item, ot } — the STUDENT names a larger whole and places cards under it

// --- Figuring It Out — Living Problem Representation (OT Sprint 1) ---
export const otFiguringStart = async (id) =>
  (await axios.post(`${API}/ot/${id}/figuring/start`)).data; // { ot, figuring }
export const otFiguringInteract = async (id, content) =>
  (await axios.post(`${API}/ot/${id}/figuring/interact`, { content })).data; // { ot, figuring, sufficiency, reveal_level, message }
export const otFiguringRevisitRespond = async (id, action, content = "") =>
  (await axios.post(`${API}/ot/${id}/figuring/revisit-respond`, { action, content })).data; // { ot, figuring }

// --- My Ideas — two-pass workflow (pass1 → knowledge map → inquiry → pass2 → construct) ---
export const otIdeasInit = async (id) =>
  (await axios.post(`${API}/ot/${id}/ideas/init`)).data;
export const otIdeasInteract = async (id, index, content, passNo = 1) =>
  (await axios.post(`${API}/ot/${id}/ideas/interact`, { index, content, pass_no: passNo })).data;
export const otIdeasSave = async (id, index, content, passNo = 1) =>
  (await axios.post(`${API}/ot/${id}/ideas/save`, { index, content, pass_no: passNo })).data;
export const otIdeasAdvance = async (id, index) =>
  (await axios.post(`${API}/ot/${id}/ideas/advance`, { index })).data;
export const otIdeasMap = async (id) =>
  (await axios.post(`${API}/ot/${id}/ideas/map`)).data;
export const otIdeasConfirmMap = async (id, items, selfAssessment = "") =>
  (await axios.post(`${API}/ot/${id}/ideas/confirm-map`, { items, self_assessment: selfAssessment })).data;
export const otIdeasInquiryPlan = async (id) =>
  (await axios.post(`${API}/ot/${id}/ideas/inquiry-plan`)).data;
export const otIdeasPause = async (id, plan) =>
  (await axios.post(`${API}/ot/${id}/ideas/pause`, plan || {})).data;
export const otIdeasResume = async (id) =>
  (await axios.post(`${API}/ot/${id}/ideas/resume`)).data;
export const otIdeasConstructGuidance = async (id) =>
  (await axios.post(`${API}/ot/${id}/ideas/construct-guidance`)).data;
export const otIdeasConstruct = async (id, content) =>
  (await axios.post(`${API}/ot/${id}/ideas/construct`, { content })).data;

// --- The Writing Studio Developmental Feedback System ---
export const submitFeedback = async (id, payload) =>
  (await axios.post(`${API}/feedback/${id}`, payload)).data;
export const feedbackEvent = (id, event, data) => {
  // fire-and-forget analytics; never block or throw into the UI
  try {
    axios.post(`${API}/feedback/${id}/event`, { event, data: data || {} }).catch(() => {});
  } catch (e) { /* ignore */ }
};
export const submitInContextFeedback = async (sessionId, payload) =>
  (await axios.post(`${API}/feedback/${sessionId}/in-context`, payload)).data;
export const fetchFeedbackDashboard = async (feedbackType) => {
  const q = feedbackType ? `?feedback_type=${feedbackType}` : "";
  return (await axios.get(`${API}/feedback/dashboard${q}`)).data;
};
export const fetchFeedbackItem = async (itemId) =>
  (await axios.get(`${API}/feedback/dashboard/${itemId}`)).data;

export const interact = async (id, payload) => {
  const { data } = await axios.post(`${API}/sessions/${id}/interact`, payload);
  return data;
};

// Ask The Writing Studio — learner-to-The Writing Studio dialogue (talk about the instruction; never a revision).
export const askCompass = async (id, message, currentDraft = "") => {
  const { data } = await axios.post(`${API}/sessions/${id}/dialogue`, {
    message,
    current_student_text: currentDraft,
  });
  return data;
};

// Sentence Craft — learner control over the episode (keep | finish_after_this | stop)
export const scControl = async (id, action) => {
  const { data } = await axios.post(`${API}/sessions/${id}/sc/control`, { action });
  return data; // { ok, preference, rerun }
};


export const classroomRegister = async (p) => (await axios.post(`${API}/classroom/register`, p)).data;
export const cCreateCourse = async (p) => (await axios.post(`${API}/classroom/courses`, p)).data;
export const cListCourses = async () => (await axios.get(`${API}/classroom/courses`)).data.courses || [];
export const cCourseDetail = async (id) => (await axios.get(`${API}/classroom/courses/${id}`)).data;
export const cCreateAssignment = async (courseId, p) => (await axios.post(`${API}/classroom/courses/${courseId}/assignments`, p)).data;
export const cUpdateCourse = async (courseId, p) => (await axios.patch(`${API}/classroom/courses/${courseId}`, p)).data;
export const cAssignmentLibrary = async () => (await axios.get(`${API}/classroom/assignments/library`)).data;
export const cCopyAssignment = async (courseId, p) => (await axios.post(`${API}/classroom/courses/${courseId}/assignments/copy`, p)).data;
export const cUpdateAssignment = async (assignmentId, p) => (await axios.patch(`${API}/classroom/assignments/${assignmentId}`, p)).data;
export const cCourseRoster = async (courseId) => (await axios.get(`${API}/classroom/courses/${courseId}/roster`)).data;
export const cSetGrade = async (courseId, p) => (await axios.put(`${API}/classroom/courses/${courseId}/grades`, p)).data;
export const cDeleteAssignment = async (assignmentId, confirmTitle) => (await axios.post(`${API}/classroom/assignments/${assignmentId}/delete`, { confirm_title: confirmTitle })).data;
export const cStudentArchiveAssignment = async (assignmentId, archived = true) => (await axios.post(`${API}/classroom/assignments/${assignmentId}/student-archive`, { archived })).data;
export const deleteOwnSession = async (id) => (await axios.post(`${API}/sessions/${id}/delete`, {})).data;
export const cArchiveAssignment = async (assignmentId, archived = true) => (await axios.post(`${API}/classroom/assignments/${assignmentId}/archive`, { archived })).data;
// Due dates are stored as YYYY-MM-DD; shown as "Due Fri, Oct 24" (and flagged once past).
export const dueLabel = (d) => { if (!d) return ""; const dt = new Date(d + (d.length === 10 ? "T23:59:59" : "")); if (isNaN(dt)) return `Due ${d}`; return `Due ${dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}`; };
export const isPastDue = (d) => { if (!d) return false; const dt = new Date(d + (d.length === 10 ? "T23:59:59" : "")); return !isNaN(dt) && dt < new Date(); };
export const cAssignmentSubmissions = async (id) => (await axios.get(`${API}/classroom/assignments/${id}/submissions`)).data;
export const cSubmissionDetail = async (sid) => (await axios.get(`${API}/classroom/submissions/${sid}`)).data;
export const cSaveFeedback = async (sid, comment) => (await axios.post(`${API}/classroom/submissions/${sid}/feedback`, { comment })).data;
export const cInviteStudents = async (courseId, emails) => (await axios.post(`${API}/classroom/courses/${courseId}/invite-students`, { emails })).data;   // teacher emails the join code + join link
// COURSE WRITING PROFILE BUILDER (Phase II-A) — teacher intention → confirmed Teacher × Course profile (not yet connected to instruction)
export const cwpGet = async (courseId) => (await axios.get(`${API}/instructional/course-builder/${courseId}`)).data;
export const cwpDescription = async (courseId, teacher_description) => (await axios.put(`${API}/instructional/course-builder/${courseId}/description`, { teacher_description })).data;
export const cwpUpload = async (courseId, file, fields) => { const fd = new FormData(); fd.append("file", file); Object.entries(fields || {}).forEach(([k, v]) => fd.append(k, v || "")); return (await axios.post(`${API}/instructional/course-builder/${courseId}/sources`, fd)).data; };
export const cwpDeleteSource = async (courseId, sourceId) => (await axios.delete(`${API}/instructional/course-builder/${courseId}/sources/${sourceId}`)).data;
export const cwpInterpret = async (courseId) => (await axios.post(`${API}/instructional/course-builder/${courseId}/interpret`)).data;
export const cwpDraft = async (courseId, draft) => (await axios.put(`${API}/instructional/course-builder/${courseId}/draft`, draft)).data;
export const cwpConfirm = async (courseId) => (await axios.post(`${API}/instructional/course-builder/${courseId}/confirm`)).data;
// ASSIGNMENT WRITING PROFILE BUILDER (Phase II-D) — Teacher × Course × Assignment; dormant for instruction
export const awpGet = async (assignmentId) => (await axios.get(`${API}/instructional/assignment-builder/${assignmentId}`)).data;
export const awpDescription = async (assignmentId, teacher_description) => (await axios.put(`${API}/instructional/assignment-builder/${assignmentId}/description`, { teacher_description })).data;
export const awpUpload = async (assignmentId, file, fields) => { const fd = new FormData(); fd.append("file", file); Object.entries(fields || {}).forEach(([k, v]) => fd.append(k, v || "")); return (await axios.post(`${API}/instructional/assignment-builder/${assignmentId}/sources`, fd)).data; };
export const awpDeleteSource = async (assignmentId, sourceId) => (await axios.delete(`${API}/instructional/assignment-builder/${assignmentId}/sources/${sourceId}`)).data;
export const awpInterpret = async (assignmentId) => (await axios.post(`${API}/instructional/assignment-builder/${assignmentId}/interpret`)).data;
export const awpDraft = async (assignmentId, draft) => (await axios.put(`${API}/instructional/assignment-builder/${assignmentId}/draft`, draft)).data;
export const awpConfirm = async (assignmentId) => (await axios.post(`${API}/instructional/assignment-builder/${assignmentId}/confirm`)).data;
export const cJoinCourse = async (join_code) => (await axios.post(`${API}/classroom/join`, { join_code })).data;
export const cMyWritingPathSession = async (entryMode = "") => (await axios.get(`${API}/classroom/my-writing-path-session`, { params: entryMode ? { entry_mode: entryMode } : {} })).data;
export const writingPathExit = async (id) => (await axios.post(`${API}/sessions/${id}/writing-path/exit`, {})).data;
export const writingPathReturnToStrengthen = async (id) => (await axios.post(`${API}/sessions/${id}/writing-path/return-to-strengthen`, {})).data;
export const writingPathStrengthenBegin = async (id, text) => (await axios.post(`${API}/sessions/${id}/writing-path/strengthen-begin`, { text })).data;
export const cStrengthenStart = async (assignment, text) => (await axios.post(`${API}/classroom/strengthen/start`, { assignment, text })).data;
export const cStandaloneStart = async (assignment) => (await axios.post(`${API}/classroom/standalone/start`, { assignment })).data;
export const cStandaloneResume = async (id) => (await axios.post(`${API}/classroom/standalone/resume/${id}`)).data;
export const cStandaloneImport = async (id) => (await axios.post(`${API}/classroom/standalone/import/${id}`)).data;
export const cMyCourses = async () => (await axios.get(`${API}/classroom/my-courses`)).data.courses || [];
export const cCourseAssignments = async (id) => (await axios.get(`${API}/classroom/courses/${id}/assignments`)).data.assignments || [];
export const cStartAssignment = async (id) => (await axios.post(`${API}/classroom/assignments/${id}/start`)).data;
export const cMySubmission = async (id) => (await axios.get(`${API}/classroom/assignments/${id}/submission`)).data;

// Phase 2B — Longitudinal Writing Development Dashboard (teacher/admin only; shadow WSR-1.3 scores).
export const cCourseDevelopment = async (courseId) => (await axios.get(`${API}/classroom/courses/${courseId}/development`)).data;
export const cSubmissionDevelopment = async (sid) => (await axios.get(`${API}/classroom/submissions/${sid}/development`)).data;
export const cMyDevelopment = async (assignmentId) => (await axios.get(`${API}/classroom/assignments/${assignmentId}/my-development`)).data;
export const cMyDevelopmentBySession = async (sid) => (await axios.get(`${API}/classroom/submissions/${sid}/my-development`)).data;
export const cMyDevelopmentHistory = async () => (await axios.get(`${API}/classroom/my-development/history`)).data;
// Instructor Assessment (Feature 2)
export const cSchemes = async (courseId) => (await axios.get(`${API}/classroom/assessment-schemes`, { params: { course_id: courseId } })).data;
export const cCreateScheme = async (body) => (await axios.post(`${API}/classroom/assessment-schemes`, body)).data;
export const cAssessConfig = async (aid) => (await axios.get(`${API}/classroom/assignments/${aid}/assessment-config`)).data;
export const cSetAssessConfig = async (aid, body) => (await axios.put(`${API}/classroom/assignments/${aid}/assessment-config`, body)).data;
export const cAssignmentAssessments = async (aid) => (await axios.get(`${API}/classroom/assignments/${aid}/assessments`)).data;
export const cGetStudentAssessment = async (aid, sid) => (await axios.get(`${API}/classroom/assignments/${aid}/students/${sid}/assessment`)).data;
export const cPutStudentAssessment = async (aid, sid, body) => (await axios.put(`${API}/classroom/assignments/${aid}/students/${sid}/assessment`, body)).data;
export const cCourseGradebook = async (cid) => (await axios.get(`${API}/classroom/courses/${cid}/gradebook`)).data;
export const cMyAssessment = async (aid) => (await axios.get(`${API}/classroom/assignments/${aid}/my-assessment`)).data;
export const cRefreshCourseAnalysis = async (courseId) => (await axios.post(`${API}/classroom/courses/${courseId}/analysis/refresh`)).data;
export const cRefreshSubmissionAnalysis = async (sid) => (await axios.post(`${API}/classroom/submissions/${sid}/analysis/refresh`)).data;
export const cCourseIstDevelopment = async (courseId) => (await axios.get(`${API}/classroom/courses/${courseId}/ist-development`)).data;
export const cSubmissionIst = async (sid) => (await axios.get(`${API}/classroom/ist/${sid}`)).data;

// ── Security pass: email verification + Google sign-in ─────────────────────────
export const verifyEmail = async (token) => (await axios.post(`${API}/auth/verify-email`, { token })).data;
export const resendVerification = async () => (await axios.post(`${API}/auth/resend-verification`)).data;
export const googleSignIn = async (payload) => (await axios.post(`${API}/auth/google`, payload)).data;
export const getAiUsage = async () => (await axios.get(`${API}/ai/usage`)).data;

// ── Writing Path (Increment 1: Thesis-First shell) ───────────────────────────
export const getWritingPath = async (id) => (await axios.get(`${API}/sessions/${id}/writing-path`)).data;
export const writingPathReadiness = async (id, choice, note = "") => (await axios.post(`${API}/sessions/${id}/writing-path/readiness`, { choice, note })).data;
export const writingPathArgument = async (id, payload) => (await axios.post(`${API}/sessions/${id}/writing-path/argument`, payload)).data;
export const writingPathOrganize = async (id, payload) => (await axios.post(`${API}/sessions/${id}/writing-path/organize`, payload)).data;
export const writingPathWrite = async (id, payload) => (await axios.post(`${API}/sessions/${id}/writing-path/write`, payload)).data;
export const writingPathBigPicture = async (id) => (await axios.get(`${API}/sessions/${id}/writing-path/big-picture`)).data;
export const writingPathRevise = async (id, payload) => (await axios.post(`${API}/sessions/${id}/writing-path/revise`, payload)).data;
export const writingPathEntry = async (id, payload) => (await axios.post(`${API}/sessions/${id}/writing-path/entry`, payload)).data;
export const writingPathIntroDismiss = async (id, transition) => (await axios.post(`${API}/sessions/${id}/writing-path/intro`, { transition })).data;
